import { useState } from 'react'

import { МАСШТАБЫ, мирПоРазмеру, ru } from '../../lib/physics'
import { Predict } from './common'

/**
 * Три мира (урок 9): шкала размеров от ядра атома (10⁻¹⁵ м) до Вселенной (10²⁶ м) —
 * больше сорока порядков. Микромир меньше атома, мегамир — больше Солнечной системы,
 * между ними макромир. У каждого мира — свой раздел физики.
 */

const ОБЪЕКТЫ = [
  { что: 'ядро атома', размер: 1e-15 },
  ...МАСШТАБЫ,
  { что: 'Солнечная система с окрестностями', размер: 1e15 },
  { что: 'Галактика', размер: 1e21 },
  { что: 'вся видимая Вселенная', размер: 1e26 },
]
const ОТ = -15
const ДО = 26
const X0 = 20
const W = 320
const xПор = (p: number) => X0 + ((p - ОТ) / (ДО - ОТ)) * W

/** 10 в степени — надстрочными цифрами: 10⁻⁹. */
function ст(n: number): string {
  const цифры = '⁰¹²³⁴⁵⁶⁷⁸⁹'
  return `10${n < 0 ? '⁻' : ''}${String(Math.abs(n)).split('').map((ц) => цифры[Number(ц)]).join('')}`
}

const РАЗДЕЛ = {
  микромир: 'квантовая физика: частицы ведут себя и как волны',
  макромир: 'классическая физика: то, что мы видим и меряем приборами',
  мегамир: 'космология: видим только прошлое — свет идёт долго',
} as const

export default function WorldsLab() {
  const [номер, setНомер] = useState(7)
  const о = ОБЪЕКТЫ[номер]
  const порядок = Math.log10(о.размер)
  const мир = мирПоРазмеру(о.размер)

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 110" role="img" aria-label="Шкала размеров: три мира">
        <rect x={xПор(ОТ)} y={30} width={xПор(-9) - xПор(ОТ)} height={20} className="fill-unit-b" />
        <rect x={xПор(-9)} y={30} width={xПор(15) - xПор(-9)} height={20} className="fill-unit-a" />
        <rect x={xПор(15)} y={30} width={xПор(ДО) - xПор(15)} height={20} className="fill-earth" />
        <text x={(xПор(ОТ) + xПор(-9)) / 2} y={24} textAnchor="middle" className="lab-tick">микро</text>
        <text x={(xПор(-9) + xПор(15)) / 2} y={24} textAnchor="middle" className="lab-tick">макро</text>
        <text x={(xПор(15) + xПор(ДО)) / 2} y={24} textAnchor="middle" className="lab-tick">мега</text>
        {[-15, -9, 0, 15, 26].map((p) => (
          <g key={p}>
            <line x1={xПор(p)} y1={50} x2={xПор(p)} y2={58} className="ink" />
            <text x={xПор(p)} y={70} textAnchor="middle" className="lab-tick">{p === 0 ? '1 м' : ст(p)}</text>
          </g>
        ))}
        <polygon points={`${xПор(порядок)},52 ${xПор(порядок) - 6},86 ${xПор(порядок) + 6},86`} className="fill-heavy" />
        <text x={Math.min(Math.max(xПор(порядок), 70), 290)} y={102} textAnchor="middle" className="lab-label heavy">{о.что}</text>
      </svg>

      <div className="lab-readout">
        <span>размер ~ {о.размер >= 1e-3 && о.размер < 1e5 ? `${ru(о.размер, о.размер < 1 ? 3 : 0)} м` : `${ст(Math.round(порядок))} м`}</span>
        <span className="heavy">{мир}</span>
      </div>
      <div className="lab-caption">{РАЗДЕЛ[мир]}</div>

      <label className="lab-slider">
        <span>Объект</span>
        <input type="range" min={0} max={ОБЪЕКТЫ.length - 1} step={1} value={номер} onChange={(e) => setНомер(Number(e.target.value))} />
      </label>

      <Predict
        question="К какому миру относится атом водорода (около 10⁻¹⁰ м)?"
        options={['микромир', 'макромир', 'мегамир']}
        right={1}
        revealed={о.что === 'атом'}
        explain="Граница микромира — 10⁻⁹ м, размер атома; движение атомов ещё описывает классическая физика, а частицы внутри атома — уже квантовая."
      />
      <p className="muted small">
        От ядра атома до Вселенной — 41 порядок: каждый шаг ползунка в десятки и тысячи раз больше прежнего.
        Граница между мирами условна: по ней меняется не природа, а то, какая физика нужна для описания.
      </p>
    </div>
  )
}
